// **The run lease** (`rounds-local-trace-store` "A run in progress is announced by a lease, so a
// staged generation is never ambiguous"; `rounds-refresh-restore` architecture review **F3**).
//
// A staged generation directory is created **eagerly**, before a single byte is appended, so its mere
// existence says nothing about whether a run is in progress or was abandoned. Two tabs share one
// device credential and therefore one store, and nothing coordinated them: reclamation at writer-store
// open was unconditional, so one tab starting a run could delete the generation another tab's worker
// was writing into *right then*. That hazard predates this change; the lease closes it.
//
// The mechanism is the Web Locks API, and the choice of it is load-bearing in one specific way: a lock
// held by a page is released by the **browser** when that page goes away, however it goes away. A
// crash, a kill, a swipe-closed tab and a lost connection all release it, with no cleanup path of ours
// running. That is exactly the property a heartbeat or a timestamp file cannot have — either would
// have to guess a staleness threshold, and both directions of that guess are harmful (see
// {@link RunLiveness}).
//
// ## Three questions, three functions
//
//   * **"I am writing this run."** — {@link acquireRunLease}, held for the whole run and released on
//     publish, on discard, or by the browser when the process ends.
//   * **"Is anyone writing this run?"** — {@link probeRunLiveness}, which reads `query()` and acquires
//     nothing. This is what the spec means by determining liveness "without acquiring the lease and
//     without reading a single byte of it": it is metadata about a directory, never a view into it.
//   * **"May I reclaim this run?"** — {@link withRunLeaseIfFree}, which *takes* the lease with
//     `ifAvailable`. Taking it is the proof: an exclusive lock granted means no writer holds one, and
//     holding it for the duration of the removal means none can be taken mid-reclaim.
//
// The probe and the take are deliberately separate. The probe is for *reporting* (the interrupted-run
// notice), where acquiring a lock as a side effect of asking a question would itself be a mutation;
// the take is for *acting*, where the answer must not be able to go stale between the check and the
// delete.

/** A granted lock, as the Web Locks API hands one to its callback. Only its presence is ever read. */
export interface LockLike {
  readonly name?: string;
}

/** One entry of `LockManager.query()`'s snapshot. */
export interface LockInfoLike {
  readonly name?: string;
}

/** What `LockManager.query()` resolves to. */
export interface LockManagerSnapshot {
  readonly held?: readonly LockInfoLike[];
  readonly pending?: readonly LockInfoLike[];
}

/**
 * **The lease facility, as an injectable seam** — the `navigator.locks` subset this path uses.
 *
 * A seam rather than a direct `navigator.locks` read for the reason every other device capability on
 * this path is one: jsdom provides no `navigator.locks` at all, and the lease is the mechanism three
 * of this capability's scenarios turn on. A test that could not model "held by another holder", "held
 * by a holder that died", and "no lock manager at all" could not bind them.
 */
export interface LockManagerLike {
  request<T>(
    name: string,
    options: { readonly mode?: 'exclusive' | 'shared'; readonly ifAvailable?: boolean },
    callback: (lock: LockLike | null) => Promise<T>,
  ): Promise<T>;
  query(): Promise<LockManagerSnapshot>;
}

/**
 * Whether a staged generation is being written.
 *
 * `unknown` is a first-class answer, not a failure to compute one. Where the runtime offers no lease
 * facility the honest report is that liveness cannot be established, and the spec requires callers to
 * treat that as **possibly live**: reclaim nothing, report nothing. Both guesses are worse than
 * declining — guessing `live` leaks bytes for ever, guessing `abandoned` deletes another tab's run
 * while it is still stepping.
 */
export type RunLiveness = 'live' | 'abandoned' | 'unknown';

/**
 * The lock name for a run.
 *
 * Namespaced, because Web Locks are **origin-scoped**: every script on the origin shares one
 * namespace, so an unprefixed run id would collide with any other consumer that happened to lock on a
 * small integer. Includes the store root so a future second trace store on the same origin cannot
 * alias this one.
 */
export function runLeaseName(runId: number): string {
  return `waggle:practice-trace:run:${runId}`;
}

/** A held lease. Releasing is idempotent, so a publish path and an unload path may both call it. */
export interface RunLease {
  readonly runId: number;
  release(): void;
}

/**
 * Takes the lease for a run and **holds it until {@link RunLease.release}**.
 *
 * Resolves to `null` when the lease is already held — which, for the writer, means another process is
 * already writing this run and this one must not — and also when the runtime offers no lock manager.
 *
 * **Those two are the same value but not the same situation, and a caller must tell them apart**
 * (implementation review **F2**; an earlier version of this comment said the writer's fallback was
 * identical, which was wrong and is the more dangerous half of that finding). Where there is no lock
 * manager, {@link probeRunLiveness} answers `unknown` for every run and callers leave the generation
 * alone, so writing without a lease is safe. Where there *is* one and it did not grant the lease, the
 * same probe answers **`abandoned`** — so a generation written unannounced is deletable by another
 * process mid-write. The lock manager is the caller's own to inspect (`locks === undefined`), which
 * is why this function does not need to return a third value; `practice.worker.ts` makes exactly that
 * distinction and refuses the run in the second case.
 *
 * The odd shape below is the Web Locks API's own: a lock lives exactly as long as the promise its
 * callback returns, so holding one past the acquiring call means handing the callback a promise
 * somebody else resolves. `request` itself is deliberately not awaited — awaiting it would mean
 * waiting for the release we are about to hand back.
 */
export async function acquireRunLease(
  locks: LockManagerLike | undefined,
  runId: number,
): Promise<RunLease | null> {
  if (locks === undefined) {
    return null;
  }
  let release!: () => void;
  const released = new Promise<void>((resolve) => {
    release = resolve;
  });
  let settle!: (granted: boolean) => void;
  const granted = new Promise<boolean>((resolve) => {
    settle = resolve;
  });
  void locks
    .request(runLeaseName(runId), { mode: 'exclusive', ifAvailable: true }, async (lock) => {
      if (lock === null || lock === undefined) {
        settle(false);
        return;
      }
      settle(true);
      await released;
    })
    // A rejected request must still settle the grant, or the caller awaits for ever. It resolves to
    // "not granted", which is the safe direction: the caller proceeds without a lease rather than
    // believing it holds one.
    .catch(() => {
      settle(false);
    });
  if (!(await granted)) {
    return null;
  }
  let releasedAlready = false;
  return {
    runId,
    release: () => {
      if (releasedAlready) {
        return;
      }
      releasedAlready = true;
      release();
    },
  };
}

/**
 * Asks whether a run is being written, **acquiring nothing**.
 *
 * `query()` reports the whole origin's locks — every tab and every worker — which is what makes this
 * answer cross-process without any channel of our own. A name in `held` means a writer has it; a name
 * in `pending` counts as live too, since a queued exclusive request means some process intends to
 * write and reclaiming underneath it would be the same data loss.
 */
export async function probeRunLiveness(
  locks: LockManagerLike | undefined,
  runId: number,
): Promise<RunLiveness> {
  if (locks === undefined) {
    return 'unknown';
  }
  const name = runLeaseName(runId);
  let snapshot: LockManagerSnapshot;
  try {
    snapshot = await locks.query();
  } catch {
    // The facility exists but would not answer. That is precisely "cannot be established" — it must
    // not collapse to `abandoned`, which would licence deleting a live run's bytes.
    return 'unknown';
  }
  const names = [...(snapshot.held ?? []), ...(snapshot.pending ?? [])];
  return names.some((info) => info.name === name) ? 'live' : 'abandoned';
}

/**
 * Runs `body` **only if** this process can take the run's lease, and holds it throughout.
 *
 * This is the reclamation gate. Taking an exclusive lock *is* the proof that no writer holds one, and
 * keeping it for the duration of the body is what stops a writer starting mid-removal — a check
 * followed by an unguarded delete would have exactly the race the lease exists to close.
 *
 * Returns `false` without running `body` when the lease could not be taken **or** when there is no
 * lock manager. The latter is the spec's "where liveness cannot be established, the generation SHALL
 * be left untouched": a runtime with no lease facility reclaims nothing.
 */
export async function withRunLeaseIfFree(
  locks: LockManagerLike | undefined,
  runId: number,
  body: () => Promise<void>,
): Promise<boolean> {
  const lease = await acquireRunLease(locks, runId);
  if (lease === null) {
    return false;
  }
  try {
    await body();
  } finally {
    lease.release();
  }
  return true;
}

/**
 * The production lease facility, or `undefined` where the runtime has none.
 *
 * `undefined` rather than a no-op stand-in: a stand-in would have to answer the liveness question, and
 * every answer it could invent is one of the two harmful guesses. Absence propagates to `unknown`,
 * which is the only truthful report.
 *
 * Reads `globalThis.navigator` rather than `navigator` so this is callable from a worker and from a
 * jsdom process with neither.
 */
export function browserLockManager(): LockManagerLike | undefined {
  const locks = (globalThis as { navigator?: { locks?: unknown } }).navigator?.locks;
  if (
    locks === undefined ||
    locks === null ||
    typeof (locks as LockManagerLike).request !== 'function' ||
    typeof (locks as LockManagerLike).query !== 'function'
  ) {
    return undefined;
  }
  return locks as LockManagerLike;
}
